/**
 * Patient Layout - Layout for Patient Portal Pages
 *
 * Similar to AppLayout but specifically for patient-facing pages
 * with patient-specific sidebar and navigation
 */

import { useEffect } from 'react'
import { usePage } from '@inertiajs/react'
import { toast } from 'sonner'
import { Toaster } from '@/components/ui/sonner'
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar'
import { PatientSidebar } from '@/components/patient-sidebar'
import { getSidebarState } from '@/lib/sidebar-state'
import { ThemeProvider } from '@/components/theme-provider'
import { ChatBubble } from '@/components/chat-bubble'

interface FlashMessages {
  success?: string
  error?: string
  notice?: string
  alert?: string
}

interface PatientUser {
  id: number
  display_name: string
  email: string
}

interface PageProps {
  flash?: FlashMessages
  patient?: PatientUser
  [key: string]: any
}

interface PatientLayoutProps {
  children: React.ReactNode
}

export function PatientLayout({ children }: PatientLayoutProps) {
  const { props } = usePage<PageProps>()
  const flash = props.flash
  const patient = props.patient

  // Show toast notifications when flash messages change
  useEffect(() => {
    if (!flash) return

    // Success toast
    if (flash.success) {
      toast.success(flash.success, {
        style: {
          '--normal-bg':
            'color-mix(in oklab, light-dark(var(--color-green-600), var(--color-green-400)) 10%, var(--background))',
          '--normal-text': 'light-dark(var(--color-green-600), var(--color-green-400))',
          '--normal-border': 'light-dark(var(--color-green-600), var(--color-green-400))'
        } as React.CSSProperties
      })
    }

    // Notice toast
    if (flash.notice) {
      toast.success(flash.notice, {
        style: {
          '--normal-bg':
            'color-mix(in oklab, light-dark(var(--color-green-600), var(--color-green-400)) 10%, var(--background))',
          '--normal-text': 'light-dark(var(--color-green-600), var(--color-green-400))',
          '--normal-border': 'light-dark(var(--color-green-600), var(--color-green-400))'
        } as React.CSSProperties
      })
    }

    // Error/alert toast
    if (flash.error || flash.alert) {
      const message = flash.error || flash.alert
      toast.error(message, {
        style: {
          '--normal-bg':
            'color-mix(in oklab, light-dark(var(--color-red-600), var(--color-red-400)) 10%, var(--background))',
          '--normal-text': 'light-dark(var(--color-red-600), var(--color-red-400))',
          '--normal-border': 'light-dark(var(--color-red-600), var(--color-red-400))'
        } as React.CSSProperties
      })
    }
  }, [flash])

  // If patient is not provided, render without sidebar
  if (!patient) {
    return (
      <ThemeProvider>
        <Toaster position="top-right" />
        {children}
      </ThemeProvider>
    )
  }

  // Authenticated patient pages with sidebar
  return (
    <ThemeProvider>
      <Toaster position="top-right" />
      <SidebarProvider
        defaultOpen={getSidebarState()}
        style={
          {
            '--sidebar-width': 'calc(var(--spacing) * 72)',
            '--header-height': 'calc(var(--spacing) * 12)',
          } as React.CSSProperties
        }
      >
        <PatientSidebar patient={patient} />
        <SidebarInset>{children}</SidebarInset>
        <ChatBubble portalType="patient" apiBasePath="/patients/ai" tokenKey="patient_session_token" />
      </SidebarProvider>
    </ThemeProvider>
  )
}
